import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Container, Row, Col, Card, Spinner, Alert, Badge, Button } from 'react-bootstrap';
import { promptsAPI } from '../services/api';
import toast from 'react-hot-toast'; // Import toast
import { Eye, Users, Lock, Tag, Calendar, User, Edit, Copy } from 'lucide-react'; // Import Copy icon

const PromptDetailPage = () => {
 const { id } = useParams();

 const { data: promptData, isLoading, error } = useQuery(
   ['prompt', id],
   () => promptsAPI.getPrompt(id),
   {
     enabled: !!id, // Only run query if id is available
     staleTime: 5 * 60 * 1000, // 5 minutes
   }
 );

 const getVisibilityIcon = (visibility) => {
   switch (visibility) {
     case 'public':
       return <Eye size={16} className="text-success" />;
     case 'team':
       return <Users size={16} className="text-info" />;
     case 'private':
       return <Lock size={16} className="text-secondary" />;
     default:
       return <Lock size={16} className="text-secondary" />;
   }
 };

 const formatDate = (dateString) => {
   return new Date(dateString).toLocaleDateString('de-DE', {
     year: 'numeric',
     month: 'short',
     day: 'numeric',
     hour: '2-digit',
     minute: '2-digit',
   });
 };

 const handleCopy = async () => {
   if (promptData?.data?.content) {
     try {
       await navigator.clipboard.writeText(promptData.data.content);
       toast.success('Prompt-Inhalt in die Zwischenablage kopiert!');
     } catch (err) {
       toast.error('Fehler beim Kopieren des Prompt-Inhalts.');
       console.error('Fehler beim Kopieren: ', err);
     }
   }
 };

 if (isLoading) {
   return (
     <Container fluid className="text-center py-5">
       <Spinner animation="border" />
       <p className="mt-3">Prompt-Details werden geladen...</p>
     </Container>
   );
 }

 if (error) {
   return (
     <Container fluid>
       <Alert variant="danger">
         <Alert.Heading>Fehler beim Laden des Prompts</Alert.Heading>
         <p>{error.message}</p>
         <Button onClick={() => window.location.reload()}>Erneut versuchen</Button>
       </Alert>
     </Container>
   );
 }

 const prompt = promptData?.data; // Access the 'data' property of the response

 if (!prompt) {
   return (
     <Container fluid className="text-center py-5">
       <Alert variant="warning">
         <Alert.Heading>Prompt nicht gefunden</Alert.Heading>
         <p>Der gesuchte Prompt existiert nicht oder du hast keine Berechtigung, ihn anzusehen.</p>
         <Link to="/prompts" className="btn btn-primary">Zurück zu den Prompts</Link>
       </Alert>
     </Container>
   );
 }

 return (
   <Container fluid>
     <Row className="mb-4 align-items-center">
       <Col>
         <h1 className="h3 mb-1">{prompt.title}</h1>
         <p className="text-muted mb-0">{prompt.description}</p>
       </Col>
       <Col xs="auto">
         {prompt?.id && (
            <>
              <Button variant="outline-primary" onClick={handleCopy} className="me-2">
                <Copy size={16} className="me-2" />
                Inhalt kopieren
              </Button>
              <Link to={`/prompts/${prompt.id}/edit`} className="btn btn-outline-secondary">
                <Edit size={16} className="me-2" />
                Prompt bearbeiten
              </Link>
            </>
          )}
        </Col>
      </Row>

      <Row>
        <Col lg={9}>
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5>Inhalt</h5>
              <div className="d-flex align-items-center gap-2">
                {getVisibilityIcon(prompt.visibility)}
                <Badge bg="secondary">{prompt.visibility === 'public' ? 'Öffentlich' : prompt.visibility === 'team' ? 'Team' : 'Privat'}</Badge>
              </div>
            </Card.Header>
            <Card.Body>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{prompt.content}</pre>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3}>
          <Card className="mb-4">
            <Card.Header>
              <h5>Metadaten</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-2">
                <User size={16} className="text-muted" />
                <strong>Autor:</strong> {prompt.author?.name || 'N/A'}
              </div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Calendar size={16} className="text-muted" />
                <strong>Erstellt:</strong> {formatDate(prompt.createdAt)}
              </div>
              {prompt.updatedAt && (
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Calendar size={16} className="text-muted" />
                  <strong>Zuletzt aktualisiert:</strong> {formatDate(prompt.updatedAt)}
                </div>
              )}
              {prompt.category && (
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Badge bg="info">{prompt.category}</Badge>
                </div>
              )}
              {prompt.tags && prompt.tags.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-3">
                  {prompt.tags.map((tag) => (
                    <Badge key={tag._id} bg="primary">
                      <Tag size={12} className="me-1" />
                      {tag.displayName}
                    </Badge>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PromptDetailPage;